import { EMAIL_PATTERN } from '../../config/constants';
import {
  DEFAULT_COMPANY_ID,
  DEFAULT_EVENT,
  DEFAULT_PAGE_NAME,
  DEFAULT_REP,
  ERROR_MESSAGE,
  MAX_EMAIL_LENGTH,
  MAX_MOBILE_LENGTH,
  MAX_NAME_LENGTH,
  MAX_NOTES_LENGTH,
  MAX_REP_LENGTH,
  MAX_ZIP_LENGTH,
  NOTES_PREFIX,
  QUERY_PARAM,
  SAVE_DEMO_ACTION,
} from './constants';

const MIN_MOBILE_DIGITS = 10;
const MAX_MOBILE_DIGITS = 15;

export function createEmptyForm() {
  return {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    zipCode: '',
    notes: '',
    receiveSms: true,
  };
}

/**
 * Event settings the rep puts in the URL once, then leaves alone for the day.
 */
export function readEventSettings(query) {
  const event = (query.get(QUERY_PARAM.EVENT) || '').trim() || DEFAULT_EVENT;
  // Stored as a number so the record matches what the Unbounce webhook writes.
  const companyId = Number((query.get(QUERY_PARAM.COMPANY_ID) || '').trim());

  return {
    companyId: Number.isFinite(companyId) && companyId > 0 ? companyId : DEFAULT_COMPANY_ID,
    promotionId: (query.get(QUERY_PARAM.PROMOTION_ID) || '').trim(),
    rep: (query.get(QUERY_PARAM.REP) || '').trim().slice(0, MAX_REP_LENGTH) || DEFAULT_REP,
    variant: (query.get(QUERY_PARAM.VARIANT) || '').trim(),
    event,
    pageName: `${DEFAULT_PAGE_NAME} - ${event}`,
  };
}

/** Keeps digits plus an optional leading +, so what we store is dialable. */
export function normalizeMobile(value) {
  const trimmed = (value || '').trim();
  const digits = trimmed.replace(/\D/g, '');

  if (!digits) {
    return '';
  }

  return trimmed.startsWith('+') ? `+${digits}` : digits;
}

export function isValidMobile(value) {
  const digits = normalizeMobile(value).replace(/\D/g, '');
  return digits.length >= MIN_MOBILE_DIGITS && digits.length <= MAX_MOBILE_DIGITS;
}

export function validateIntakeForm(form) {
  if (!form.firstName.trim()) {
    return ERROR_MESSAGE.FIRST_NAME;
  }
  if (!form.lastName.trim()) {
    return ERROR_MESSAGE.LAST_NAME;
  }

  const email = form.email.trim();
  if (!email || !EMAIL_PATTERN.test(email)) {
    return ERROR_MESSAGE.EMAIL;
  }

  if (!isValidMobile(form.mobile)) {
    return ERROR_MESSAGE.MOBILE;
  }

  return '';
}

function localDateParts(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');

  return {
    dateSubmitted: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    timeSubmitted: `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
  };
}

/**
 * Same record the Unbounce webhook writes via saveStartingDemoDetails, so a
 * trailer walk-up and a landing-page lead look identical in Booth Minder.
 */
export function buildIntakePayload({ form, settings, pageUrl, now }) {
  const email = form.email.trim().toLowerCase().slice(0, MAX_EMAIL_LENGTH);
  const mobile = normalizeMobile(form.mobile).slice(0, MAX_MOBILE_LENGTH);
  const zipCode = form.zipCode.trim().slice(0, MAX_ZIP_LENGTH);
  const notes = form.notes.trim().slice(0, MAX_NOTES_LENGTH);
  const { dateSubmitted, timeSubmitted } = localDateParts(now);

  const initialData = {
    firstName: form.firstName.trim().slice(0, MAX_NAME_LENGTH),
    lastName: form.lastName.trim().slice(0, MAX_NAME_LENGTH),
    email,
    mobile,
    companyId: settings.companyId,
    postcode: zipCode,
    zipCode,
    // enrollDemoProspectWithAgent checks `receiveSms === true`, so keep it boolean.
    receiveSms: form.receiveSms,
    notes: notes || `${NOTES_PREFIX}${pageUrl}`,
    pageName: settings.pageName,
    pageUrl,
    variant: settings.variant,
    promotionId: settings.promotionId,
    blackBoxVrRep: settings.rep,
    dateSubmitted,
    timeSubmitted,
  };

  return {
    action: SAVE_DEMO_ACTION,
    id: email,
    initialData,
  };
}

export function extractIntakeErrorMessage(error) {
  if (typeof error === 'string' && error) {
    return error;
  }
  if (typeof error?.data?.errorMessage === 'string' && error.data.errorMessage) {
    return error.data.errorMessage;
  }
  if (typeof error?.data?.error === 'string' && error.data.error) {
    return error.data.error;
  }
  if (typeof error?.data === 'string' && error.data) {
    return error.data;
  }
  if (typeof error?.errorMessage === 'string' && error.errorMessage) {
    return error.errorMessage;
  }
  if (typeof error?.message === 'string' && error.message) {
    return error.message;
  }
  return ERROR_MESSAGE.SUBMIT;
}
